import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, Clock, Target, Brain, Calculator, BookOpen,
  CheckCircle, XCircle, MinusCircle, BarChart3, Landmark,
  Monitor, ChevronLeft, ChevronRight, Timer, AlertTriangle, Zap
} from "lucide-react";

const BankingSectionalTestPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState("select");
  const [loading, setLoading] = useState(false);
  const [patterns, setPatterns] = useState([]);

  const [sessionId, setSessionId] = useState(null);
  const [sectionName, setSectionName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const timerRef = useRef(null);
  const [results, setResults] = useState(null);

  const sectionIcons = {
    quant: Calculator, reasoning: Brain, english: BookOpen,
    di: BarChart3, banking_awareness: Landmark, computer: Monitor
  };

  useEffect(() => { loadPatterns(); }, []);

  useEffect(() => {
    if (phase === "test" && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);

  const loadPatterns = async () => {
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/sectional-patterns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPatterns(data.patterns || []);
    } catch { toast({ variant: "destructive", description: "Failed to load" }); }
  };

  const startTest = async (sectionId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/start-sectional?section=${sectionId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setSectionName(data.section_name);
      setQuestions(data.questions);
      setTotalTime(data.time_minutes * 60);
      setTimeLeft(data.time_minutes * 60);
      setCurrentQ(0);
      setAnswers({});
      setPhase("test");
    } catch { toast({ variant: "destructive", description: "Failed to start" }); }
    finally { setLoading(false); }
  };

  const handleSubmit = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setLoading(true);
    try {
      const timeTaken = totalTime - timeLeft;
      const res = await fetch(`${API_BASE}/banking/mock-tests/submit/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers, questions, time_taken: timeTaken, negative_marking: 0.25, marks_per_question: 1 })
      });
      const data = await res.json();
      setResults(data);
      setPhase("results");
    } catch { toast({ variant: "destructive", description: "Submit failed" }); }
    finally { setLoading(false); }
  }, [sessionId, answers, questions, totalTime, timeLeft, token]);

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const currentQuestion = questions[currentQ];

  // ─── SELECT ───
  if (phase === "select") {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/banking")} data-testid="sectional-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Sectional Speed Tests</h1>
              <p className="text-sm text-slate-500">Timed section tests with negative marking (-0.25)</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-sm text-blue-800">Build speed and accuracy for each section. These tests simulate the exact time pressure of real banking exams.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {patterns.map((p) => {
              const Icon = sectionIcons[p.id] || Target;
              return (
                <Card key={p.id} className="hover:shadow-lg transition-all hover:border-emerald-300" data-testid={`sectional-${p.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-3 rounded-xl bg-emerald-50"><Icon className="w-6 h-6 text-emerald-600" /></div>
                      <Badge variant="outline" className="text-xs">{p.available_questions} Qs available</Badge>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-1">{p.name}</h3>
                    <div className="flex gap-3 text-xs text-slate-500 mb-4">
                      <span>{p.questions} Questions</span>
                      <span>{p.time_minutes} Minutes</span>
                      <span className="text-red-500">-0.25 penalty</span>
                    </div>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => startTest(p.id)} disabled={loading || p.available_questions < 5} data-testid={`start-sectional-${p.id}`}>
                      {p.available_questions < 5 ? "Not enough Qs" : "Start Test"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── TEST ───
  if (phase === "test" && currentQuestion) {
    const isLowTime = timeLeft < 120;
    return (
      <div className="min-h-screen bg-slate-50">
        <div className={`sticky top-0 z-50 px-4 py-3 flex items-center justify-between border-b ${isLowTime ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
          <Badge variant="outline">{sectionName}</Badge>
          <div className={`font-mono text-lg font-bold ${isLowTime ? 'text-red-600 animate-pulse' : 'text-slate-800'}`}>
            <Timer className="w-5 h-5 inline mr-1" />{formatTime(timeLeft)}
          </div>
          <Button size="sm" variant="destructive" onClick={() => { if (window.confirm("Submit now?")) handleSubmit(); }} data-testid="submit-sectional-btn">
            Submit ({Object.keys(answers).length}/{questions.length})
          </Button>
        </div>
        <div className="w-full h-1 bg-slate-200"><div className={`h-full ${isLowTime ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${(timeLeft/totalTime)*100}%` }} /></div>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {/* Navigator */}
          <div className="flex flex-wrap gap-1.5 justify-center p-3 bg-white rounded-xl border">
            {questions.map((q, idx) => (
              <button key={idx} onClick={() => setCurrentQ(idx)}
                className={`w-8 h-8 rounded text-xs font-bold ${currentQ === idx ? "bg-emerald-600 text-white ring-2 ring-emerald-300" : answers[q.id] ? "bg-emerald-100 text-emerald-800" : "bg-white text-slate-600 border border-slate-200"}`}>
                {idx+1}
              </button>
            ))}
          </div>

          <Card>
            <CardContent className="p-6">
              <div className="flex gap-2 mb-3">
                <Badge variant="outline" className="text-xs">Q {currentQ+1}/{questions.length}</Badge>
                <Badge variant="outline" className="text-xs capitalize">{currentQuestion.difficulty}</Badge>
              </div>
              <p className="text-lg text-slate-900 leading-relaxed whitespace-pre-wrap">{currentQuestion.question}</p>
              <div className="space-y-3 mt-5">
                {currentQuestion.options?.map((option) => {
                  const optKey = typeof option === 'object' ? option.key : option;
                  const optText = typeof option === 'object' ? option.text : option;
                  const isSelected = answers[currentQuestion.id] === optKey;
                  return (
                    <button key={optKey} onClick={() => setAnswers({...answers, [currentQuestion.id]: optKey})}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}>
                      <span className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isSelected ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>{optKey.toUpperCase()}</span>
                        <span className="text-slate-700">{optText}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.max(0, currentQ-1))} disabled={currentQ === 0}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                </Button>
                <Button variant="outline" size="sm" onClick={() => { const {[currentQuestion.id]: _, ...rest} = answers; setAnswers(rest); }}>Clear</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.min(questions.length-1, currentQ+1))} disabled={currentQ === questions.length-1}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── RESULTS ───
  if (phase === "results" && results) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
          <h1 className="text-2xl font-semibold text-slate-900">{sectionName} — Results</h1>

          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-8">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                <div><p className="text-4xl font-bold text-emerald-700">{results.total_marks}</p><p className="text-sm text-slate-600">Total Marks</p><p className="text-xs text-slate-400">out of {results.max_marks}</p></div>
                <div><p className="text-3xl font-bold text-green-600">{results.correct}</p><p className="text-sm text-slate-600">Correct</p><p className="text-xs text-green-600">+{results.positive_marks}</p></div>
                <div><p className="text-3xl font-bold text-red-600">{results.wrong}</p><p className="text-sm text-slate-600">Wrong</p><p className="text-xs text-red-600">-{results.negative_marks}</p></div>
                <div><p className="text-3xl font-bold text-slate-500">{results.unattempted}</p><p className="text-sm text-slate-600">Skipped</p></div>
                <div><p className="text-3xl font-bold text-blue-600">{results.percentage}%</p><p className="text-sm text-slate-600">Score</p><p className="text-xs text-slate-400">{Math.floor(results.time_taken/60)}m taken</p></div>
              </div>
            </CardContent>
          </Card>

          {/* Review */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Answer Review</h3>
              <div className="space-y-3">
                {questions.map((q, idx) => {
                  const r = results.results?.find(x => x.question_id === q.id);
                  const userAns = answers[q.id];
                  const isCorrect = r?.status === "correct";
                  const isWrong = r?.status === "wrong";
                  return (
                    <div key={idx} className={`p-3 rounded-lg border ${isCorrect ? 'border-green-200 bg-green-50' : isWrong ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="flex items-start gap-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isCorrect ? 'bg-green-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-slate-300 text-white'}`}>{idx+1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800 line-clamp-1">{q.question}</p>
                          <div className="flex gap-3 text-xs mt-1">
                            {userAns && <span className={isCorrect ? 'text-green-700' : 'text-red-700'}>Your: {userAns.toUpperCase()}</span>}
                            <span className="text-green-700 font-medium">Ans: {r?.correct_answer?.toUpperCase()}</span>
                            {isWrong && <span className="text-red-500">-0.25</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setPhase("select"); setResults(null); }}>Take Another Test</Button>
            <Button variant="outline" onClick={() => navigate("/banking")}>Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BankingSectionalTestPage;
